'use client';

import {
  CHAT_MESSAGE_TEXT_INSET_LEFT_CLASS,
  CHAT_TOOL_ACTIVITY_ROW_Y_CLASS,
} from '@/components/chat/chat-message-layout';
import { SubAgentAvatarStrip } from '../sub-agent-avatar-strip';
import type { ActiveClusterWorker } from '../message-roles/assistant-active-workers';
import { AgentActivityMark } from '@/components/chat/agent-activity-mark';
import { getPendingAssistantCopy } from '@/i18n/pending-assistant-copy';
import { ActivityStatusText } from '@/components/chat/activity-status-text';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  resolveActivityStatusPresentation,
  resolveToolActivityStatusPhase,
} from '@/components/chat/activity-status-presentation';
import { getToolActivityCountLabel } from '@/i18n/tool-activity-copy';
import { cn } from '@/lib/utils';
import { AlertCircle, ChevronRight } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useLocale } from 'next-intl';
import { useState, type ReactNode } from 'react';
import {
  getToolActivityClusterMemberIds,
  recallToolActivityClusterIntent,
  rememberToolActivityClusterIntent,
  resolveToolActivityClusterOpenState,
  toggleToolActivityClusterIntent,
} from './tool-activity-cluster-state';
import { useToolActivityClusterFlat } from './tool-activity-cluster-flat';

const EMPTY_ACTIVE_WORKERS: ActiveClusterWorker[] = [];

export const TOOL_ROW_EASE = [0.22, 1, 0.36, 1] as const;
export const TOOL_ROW_EXPAND_TRANSITION = {
  height: { duration: 0.28, ease: TOOL_ROW_EASE },
  opacity: { duration: 0.18, ease: TOOL_ROW_EASE },
};
// Expanded child tool icons stay on the compact activity rail while the
// collapsible title itself follows the shared transcript text axis.
const TOOL_GROUP_ACTIVITY_RAIL_CLASS = 'ml-[18px] w-[calc(100%-18px)]';
const TOOL_GROUP_ACTIVITY_RAIL_INSET_CLASS = 'pl-[14px]';
// Expanded tool rows share one parent-owned vertical rhythm. Individual tool
// renderers may use icons, grids, or buttons internally, but they should not
// add top/bottom margins for spacing between rows. Add new row types with
// `data-tool-row` and let this gap + margin reset keep mixed tool clusters even.
const TOOL_GROUP_ACTIVITY_ROW_GAP_CLASS =
  'gap-1.5 [&_.flex-col:has([data-tool-row]):not([data-tool-row])]:gap-1.5';
// Compact rows and their immediate group wrappers must not add a second
// vertical rhythm. Custom artifact cards retain their internal padding.
const TOOL_GROUP_ACTIVITY_ROW_NORMALIZE_CLASS =
  '[&_div:has(>[data-tool-row])]:!my-0 [&_[data-tool-row]:not([data-tool-row-layout=custom])]:!py-0 [&_[data-tool-row]]:!my-0 [&_[data-tool-row]]:!mb-0 [&_[data-tool-row]]:max-w-full [&_[data-tool-row]]:min-w-0 [&_[data-tool-row]]:overflow-hidden [&_[data-tool-row]]:px-0 [&_[data-tool-row]]:!text-sm [&_[data-tool-row]_*]:!text-sm [&_[data-tool-row]>*]:min-w-0 [&_[data-tool-row]:not([data-tool-row-layout=custom])>:first-child]:inline-flex [&_[data-tool-row]:not([data-tool-row-layout=custom])>:first-child]:size-4 [&_[data-tool-row]:not([data-tool-row-layout=custom])>:first-child]:shrink-0 [&_[data-tool-row]:not([data-tool-row-layout=custom])>:first-child]:items-center [&_[data-tool-row]:not([data-tool-row-layout=custom])>:first-child]:justify-center';

export type ToolActivityClusterEntry =
  | {
      key: string;
      kind: 'node';
      node: ReactNode;
      importance?: 'default' | 'prominent';
      tools?: unknown[];
      leadingVisual?: ReactNode;
    }
  | { key: string; kind: 'text'; text: string };

type ProminentToolActivityClusterEntry = Extract<
  ToolActivityClusterEntry,
  { kind: 'node' }
> & {
  importance: 'prominent';
};

function isProminentToolActivityClusterEntry(
  entry: ToolActivityClusterEntry
): entry is ProminentToolActivityClusterEntry {
  return entry.kind === 'node' && entry.importance === 'prominent';
}

export function ToolActivityCluster({
  id,
  entries,
  summary = 'Completed',
  isPending = false,
  hasError = false,
  activityCount = entries.length,
  activeWorkers = EMPTY_ACTIVE_WORKERS,
  activeWorkerTotal,
  className,
}: {
  id: string;
  entries: Array<ToolActivityClusterEntry>;
  summary?: string;
  isPending?: boolean;
  hasError?: boolean;
  activityCount?: number;
  activeWorkers?: ActiveClusterWorker[];
  /** True in-flight count when `activeWorkers` is a loaded sample. */
  activeWorkerTotal?: number;
  className?: string;
}) {
  const reduceMotion = useReducedMotion();
  const locale = useLocale();
  const flat = useToolActivityClusterFlat();
  const memberIds = getToolActivityClusterMemberIds({
    clusterId: id,
    entryKeys: entries.map((entry) => entry.key),
  });
  const [userIntent, setUserIntent] = useState(() =>
    recallToolActivityClusterIntent(memberIds)
  );
  if (userIntent) {
    rememberToolActivityClusterIntent(memberIds, userIntent);
  }
  const open = resolveToolActivityClusterOpenState({
    userIntent,
    activityCount,
  });
  const toggleOpen = () => {
    setUserIntent((current) => {
      const next = toggleToolActivityClusterIntent(current, activityCount);
      rememberToolActivityClusterIntent(memberIds, next);
      return next;
    });
  };
  const prominentEntries = entries.filter(isProminentToolActivityClusterEntry);
  const detailEntries = entries.filter(
    (entry) => entry.kind !== 'node' || entry.importance !== 'prominent'
  );
  const hasDetailEntries = detailEntries.length > 0;
  const showDetailEntriesFirst = open && detailEntries.length > 0;
  const railEntries = showDetailEntriesFirst ? entries : detailEntries;
  const standaloneProminentEntries = showDetailEntriesFirst
    ? []
    : prominentEntries;
  const latestLeadingVisual = [...entries]
    .reverse()
    .find(
      (entry): entry is Extract<ToolActivityClusterEntry, { kind: 'node' }> =>
        entry.kind === 'node' && entry.leadingVisual !== undefined
    )?.leadingVisual;
  const hasGenericPendingSummary =
    isPending &&
    [
      getPendingAssistantCopy(locale).thinking,
      getPendingAssistantCopy(locale).working,
    ].includes(summary);
  const showThinkingBlob = isPending;
  const activeSummary = activeWorkers
    .map((worker) => worker.summary)
    .join(' · ');
  const headerSummary = activeSummary || summary;
  const phase = resolveToolActivityStatusPhase({
    isPending: isPending || activeWorkers.length > 0,
    hasError,
  });
  const presentation = resolveActivityStatusPresentation(phase);
  const showLeadingVisual = presentation.leadingVisual === 'visible';
  // Disclosure state and child jobs never remove the cluster status identity.
  const activityCountLabel = getToolActivityCountLabel(locale, activityCount);

  if (entries.length === 0) return null;

  if (flat) {
    return (
      <div
        data-tool-cluster
        data-tool-cluster-id={id}
        className={cn(
          'flex w-full max-w-full min-w-0 flex-col',
          CHAT_MESSAGE_TEXT_INSET_LEFT_CLASS,
          TOOL_GROUP_ACTIVITY_ROW_GAP_CLASS,
          TOOL_GROUP_ACTIVITY_ROW_NORMALIZE_CLASS,
          className
        )}
      >
        {/* No header here, but progress is still announced. */}
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {headerSummary}
        </span>
        {entries.map((entry) => (
          <div
            key={entry.key}
            className="w-full max-w-full min-w-0 overflow-hidden py-0 empty:hidden [&:has(>div:only-child:empty)]:hidden"
          >
            {entry.kind === 'text' ? (
              <ToolActivityText text={entry.text} />
            ) : (
              entry.node
            )}
          </div>
        ))}
      </div>
    );
  }

  const cluster = (
    <div
      className={cn(
        'w-full max-w-full min-w-0 overflow-hidden select-none',
        className
      )}
      data-tool-cluster
      data-tool-cluster-id={id}
    >
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {headerSummary}
      </span>
      {hasDetailEntries ? (
        <button
          type="button"
          onClick={toggleOpen}
          className={cn(
            'group flex w-full max-w-full min-w-0 cursor-pointer items-center gap-2 overflow-hidden rounded-lg pr-2.5 text-left focus:outline-none',
            CHAT_TOOL_ACTIVITY_ROW_Y_CLASS,
            CHAT_MESSAGE_TEXT_INSET_LEFT_CLASS
          )}
          aria-expanded={open}
          aria-label={`${headerSummary}, ${activityCountLabel}`}
          title={headerSummary}
          data-activity-count={activityCount}
          data-pending={isPending ? 'true' : undefined}
        >
          {activeWorkers.length > 0 ? (
            <SubAgentAvatarStrip
              workers={activeWorkers}
              total={activeWorkerTotal}
            />
          ) : showThinkingBlob ? (
            <AgentActivityMark state="thinking" />
          ) : showLeadingVisual && latestLeadingVisual ? (
            <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-start">
              {latestLeadingVisual}
            </span>
          ) : null}
          <span
            className="text-muted-foreground group-hover:text-foreground/65 inline-flex min-w-0 items-center gap-2 overflow-hidden text-sm transition-colors"
            style={{ maxWidth: 'min(40ch, calc(100% - 2rem))' }}
          >
            {showLeadingVisual && hasError && !latestLeadingVisual ? (
              <span className="inline-flex size-4 shrink-0 items-center justify-center self-center leading-none">
                <AlertCircle className="text-muted-foreground/70 size-3.5" />
              </span>
            ) : null}
            <span
              aria-hidden="true"
              className="block max-w-full min-w-0 truncate overflow-hidden whitespace-nowrap"
            >
              <ActivityStatusText
                phase={phase}
                animate={isPending && !hasGenericPendingSummary}
                text={headerSummary}
                className={cn(
                  'block max-w-full min-w-0 truncate text-sm',
                  // The shimmer is an inline-block nested inside this line.
                  // Top alignment keeps its baseline descender from making
                  // the loading label taller than the adjacent chevron slot.
                  presentation.text === 'shimmer' && 'align-top'
                )}
              />
            </span>
          </span>
          <span
            data-tool-chevron-slot
            className="inline-flex size-4 shrink-0 items-center justify-center leading-none"
          >
            <ChevronRight
              className={cn(
                'text-muted-foreground/45 group-hover:text-muted-foreground/70 size-3.5 motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out',
                open && 'rotate-90'
              )}
            />
          </span>
        </button>
      ) : null}
      <AnimatePresence initial={false}>
        {showDetailEntriesFirst ? (
          <motion.div
            key="activity-list"
            initial={reduceMotion ? false : { height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { height: 0 }}
            transition={TOOL_ROW_EXPAND_TRANSITION}
            style={{ overflow: 'hidden' }}
          >
            <ScrollArea
              className={cn(
                'border-border/40 mt-0.5 max-h-[min(24rem,50dvh)] max-w-full min-w-0 overflow-hidden border-l-2 pr-1',
                TOOL_GROUP_ACTIVITY_RAIL_CLASS,
                TOOL_GROUP_ACTIVITY_RAIL_INSET_CLASS
              )}
              containOverscrollWhenScrollable
              viewportClassName="max-h-[min(24rem,50dvh)] overflow-x-hidden [&>div]:!block [&>div]:!w-full [&>div]:!min-w-0"
              viewportProps={{
                role: 'group',
                tabIndex: 0,
                'aria-label': headerSummary,
              }}
              showHorizontalScrollbar={false}
              maskHeight={0}
            >
              <div>
                <div
                  className={cn(
                    // Keep every expanded cluster row in this single rail.
                    // Detail rows and prominent generation rows used to render
                    // in separate containers, which made product sidechat tool
                    // timelines look uneven. If row density changes, update the
                    // shared gap constant instead of adding per-row margins.
                    'flex w-full max-w-full min-w-0 flex-col py-0.5',
                    TOOL_GROUP_ACTIVITY_ROW_GAP_CLASS,
                    TOOL_GROUP_ACTIVITY_ROW_NORMALIZE_CLASS
                  )}
                >
                  {railEntries.map((entry) => (
                    <div
                      key={entry.key}
                      className="w-full max-w-full min-w-0 overflow-hidden py-0 empty:hidden [&:has(>div:only-child:empty)]:hidden"
                    >
                      {entry.kind === 'text' ? (
                        <ToolActivityText text={entry.text} />
                      ) : (
                        entry.node
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </ScrollArea>
          </motion.div>
        ) : null}
      </AnimatePresence>
      {standaloneProminentEntries.length > 0 ? (
        <div
          className={cn(
            'mt-1 flex w-full max-w-full min-w-0 flex-col overflow-hidden',
            TOOL_GROUP_ACTIVITY_ROW_GAP_CLASS,
            TOOL_GROUP_ACTIVITY_ROW_NORMALIZE_CLASS
          )}
        >
          {standaloneProminentEntries.map((entry) => (
            <div
              key={`prominent-${entry.key}`}
              className="w-full max-w-full min-w-0 overflow-hidden py-0 empty:hidden [&:has(>div:only-child:empty)]:hidden"
            >
              {entry.node}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="w-full max-w-full min-w-0 overflow-hidden">{cluster}</div>
  );
}

function ToolActivityText({ text }: { text: string }) {
  // Mid-turn commentary lives inside the activity stripe as a quiet section
  // header. Plain text only — markdown is intentionally skipped to keep
  // these short forecasts visually subordinate to the tool rows around
  // them. Final response text renders OUTSIDE the cluster.
  return (
    <p
      data-tool-row
      className="text-muted-foreground/90 max-w-full min-w-0 overflow-hidden text-sm leading-relaxed break-words first:mt-0"
    >
      {text}
    </p>
  );
}
